// SDK › Instrumentation (D114, APS-428; the mock's #/instrumentation, design/console-d/views/
// instrumentation.js, as Mosaic mapped it onto spec 3872a74): the SDK capabilities the policy can
// change without an app release, each a tile. An instrument's base state is its family's mode
// (spec 15.3); a staged rollout is a layer (12.4) whose `changes` set the instrument's keys for a
// share of the devices it targets, with a holdback group and guardrails the backend watches.
// The API stores no instrument: a layer belongs to the instrument whose keys it changes.
import type {
  InstrumentationFamily,
  Layer,
  LayerInput,
  LayerWhen,
  SignalFamily,
} from '../api/types'
import type { Draft } from './signals'

export const INSTRUMENT_GROUPS = ['stability', 'performance', 'experience', 'context'] as const
export type InstrumentGroup = (typeof INSTRUMENT_GROUPS)[number]

/** A policy key of `capture` an instrument also sets when it rolls out (12.2). */
type CaptureKey = 'network_spans' | 'replay' | 'logs_min_severity'

export interface Instrument {
  id: string
  group: InstrumentGroup
  /** The family whose mode is the instrument's base state; absent for Crash reporting. */
  family?: SignalFamily
  capture?: CaptureKey
  /** Crash reporting: always on, the policy cannot turn it off. */
  locked?: true
}

/** The tiles in the mock's order, only those the policy can change (Mosaic's option A). */
export const INSTRUMENTS = [
  { id: 'crash', group: 'stability', locked: true },
  { id: 'errors', group: 'stability', family: 'errors' },
  { id: 'network', group: 'performance', family: 'network', capture: 'network_spans' },
  { id: 'spans', group: 'performance', family: 'spans' },
  { id: 'metrics', group: 'performance', family: 'metrics' },
  { id: 'replay', group: 'experience', family: 'replay', capture: 'replay' },
  { id: 'logs', group: 'context', family: 'logs', capture: 'logs_min_severity' },
  { id: 'resources', group: 'context', family: 'resources' },
] as const satisfies readonly Instrument[]
export type InstrumentId = (typeof INSTRUMENTS)[number]['id']

const byId = (id: string): Instrument | undefined => INSTRUMENTS.find((x) => x.id === id)

/** The rollout stops the mock's slider walks, in percent. */
export const ROLLOUT_STOPS = [1, 5, 25, 50, 100] as const
export const HOLDBACK_DEFAULT = 5
export const HOLDBACK_MAX = 50
export const MIN_SESSIONS_DEFAULT = 200

/**
 * The instruments a layer belongs to: each whose family or capture key its changes set (a replay
 * or masking change is replay's). A server layer can set several, and shows under each.
 */
export function instrumentsOf(layer: { changes?: Layer['changes'] | undefined }): InstrumentId[] {
  const families = layer.changes?.instrumentation?.families ?? {}
  const capture = layer.changes?.capture ?? {}
  const out: InstrumentId[] = []
  for (const x of INSTRUMENTS as readonly Instrument[]) {
    if (x.locked) continue
    const mine =
      (x.family !== undefined && x.family in families) ||
      (x.capture !== undefined && x.capture in capture) ||
      (x.id === 'replay' &&
        (layer.changes?.replay !== undefined || layer.changes?.masking !== undefined))
    if (mine) out.push(x.id as InstrumentId)
  }
  return out
}

/** What a tile says: Mosaic's state words, from the base policy and the instrument's layer. */
export type TileState =
  | { kind: 'always' }
  | { kind: 'on' }
  | { kind: 'off' }
  | { kind: 'rolling'; percent: number }
  | { kind: 'rolled_back'; reason: string | null }
  | { kind: 'stopped'; reason: string | null }
  | { kind: 'expired' }

/** A family's base state: on unless the policy holds it on the device for good (`never`). */
export function baseOn(draft: Draft, family: SignalFamily): boolean {
  return draft.families[family].mode !== 'never'
}

/**
 * The layer the console edits for an instrument: its active one, else its latest in history (the
 * API lists the active layers first, in policy order, then the history newest first).
 */
export function layerOf(layers: readonly Layer[], id: InstrumentId): Layer | undefined {
  const mine = layers.filter((l) => instrumentsOf(l).includes(id))
  return mine.find((l) => l.state === 'active') ?? mine.find((l) => l.state !== 'active')
}

/**
 * A layer as PUT takes it: no state, and no id for a new one (the server assigns it). Guardrails
 * only with a holdback: they compare the two groups, so the API refuses them without one.
 */
export type LayerBody = LayerInput

export function tileState(draft: Draft, layers: readonly Layer[], id: InstrumentId): TileState {
  const x = byId(id)
  if (!x || x.locked || !x.family) return { kind: 'always' }
  const layer = layerOf(layers, id)
  if (layer?.state === 'active') {
    const percent = layer.rollout?.percent ?? 100
    return percent >= 100 ? { kind: 'on' } : { kind: 'rolling', percent }
  }
  if (layer?.state === 'rolled_back') return { kind: 'rolled_back', reason: layer.stopped_reason }
  if (layer?.state === 'stopped') return { kind: 'stopped', reason: layer.stopped_reason }
  if (layer?.state === 'expired') return { kind: 'expired' }
  return baseOn(draft, x.family) ? { kind: 'on' } : { kind: 'off' }
}

/**
 * What an instrument does on one release (the mock's Instruments by release), resolved as the
 * device panel resolves it: the instrument's layer (its active one, else its latest), when that
 * layer targets the release (a layer for every version included); else its family's mode. A
 * rolled back or stopped layer reads so on the releases it targeted. A layer that also targets an
 * OS, a model, installs or app users reaches only some of a release's devices.
 */
export type ReleaseCell =
  | { kind: 'always' }
  | { kind: 'on' }
  | { kind: 'off' }
  | { kind: 'rolling'; percent: number }
  | { kind: 'some' }
  | { kind: 'rolled_back' }
  | { kind: 'stopped' }

export function releaseCell(
  draft: Draft,
  layers: readonly Layer[],
  id: InstrumentId,
  release: string,
): ReleaseCell {
  const x = byId(id)
  if (!x || x.locked || !x.family) return { kind: 'always' }
  const base: ReleaseCell = baseOn(draft, x.family) ? { kind: 'on' } : { kind: 'off' }
  const layer = layerOf(layers, id)
  const targets = (l: Layer) => !l.when.app_version?.length || l.when.app_version.includes(release)
  if (!layer || !targets(layer)) return base
  if (layer.state === 'rolled_back') return { kind: 'rolled_back' }
  if (layer.state === 'stopped') return { kind: 'stopped' }
  if (layer.state !== 'active') return base
  const { app_version: _, ...rest } = layer.when
  if (Object.values(rest).some((v) => (Array.isArray(v) ? v.length > 0 : v !== undefined)))
    return { kind: 'some' }
  const percent = layer.rollout?.percent ?? 100
  return percent >= 100 ? { kind: 'on' } : { kind: 'rolling', percent }
}

/**
 * The releases the table shows: the newest `n` by first session, each with its share of the
 * window's sessions (its adoption), most recent first.
 */
export function recentReleases(
  rows: readonly { release: string; sessions: number; first_seen: string }[],
  n = 4,
): { release: string; share: number }[] {
  const total = rows.reduce((s, r) => s + r.sessions, 0)
  return [...rows]
    .sort((a, b) => b.first_seen.localeCompare(a.first_seen))
    .slice(0, n)
    .map((r) => ({ release: r.release, share: total > 0 ? r.sessions / total : 0 }))
}

/** The share of devices a tile fills, for the mock's bar (0 to 100). */
export function tileFill(s: TileState): number {
  if (s.kind === 'always' || s.kind === 'on') return 100
  if (s.kind === 'rolling') return s.percent
  return 0
}

/**
 * What a rolled-out instrument sets: its family's policy and, where it has one, its capture key.
 * A capture key only narrows what the app set in code (spec 12.2): `false` (or a higher severity
 * for logs) turns collection off; left out, the app's choice stands, so a layer never turns on
 * what the app turned off.
 */
export interface InstrumentSettings {
  family: InstrumentationFamily
  capture?: boolean | string
}

/** The settings a new rollout starts from: the family sent always, the app's capture as it is. */
export function defaultSettings(_id: InstrumentId): InstrumentSettings {
  return { family: { mode: 'always' } }
}

/** A layer's settings for its instrument, read back from its changes. */
export function settingsOf(layer: Layer, id: InstrumentId): InstrumentSettings {
  const x = byId(id)
  const family = x?.family ? layer.changes.instrumentation?.families[x.family] : undefined
  const capture = x?.capture ? layer.changes.capture?.[x.capture] : undefined
  return {
    family: family ?? { mode: 'always' },
    ...(capture === undefined ? {} : { capture: capture as boolean | string }),
  }
}

/** The rollout an editor holds for one instrument: the layer's fields the panel sets. */
export interface RolloutDraft {
  percent: number
  holdbackPercent: number
  /** What it targets, the stored installs and users as their hints until replaced. */
  when: LayerWhen
  guardrails: Layer['guardrails']
  settings: InstrumentSettings
  expiresAt: string | null
  /**
   * The installs and app users the layer targets: `kept` as stored (the API shows only their last
   * characters, so the body leaves them out and the API keeps them), or `replaced` by the plain
   * ids the person typed.
   */
  subjects: 'kept' | 'replaced'
}

/** The targets a layer names by install or app user: hints when read, ids when typed. */
const SUBJECT_KEYS = ['installs', 'users'] as const

/** A layer's targets with its installs and users as the API shows them: their hints. */
function withHints(layer: Layer): LayerWhen {
  return {
    ...layer.when,
    ...(layer.hints.installs.length > 0 ? { installs: layer.hints.installs } : {}),
    ...(layer.hints.users.length > 0 ? { users: layer.hints.users } : {}),
  }
}

/** A layer's `when` without its installs and users. */
export function withoutSubjects(when: LayerWhen): LayerWhen {
  const { installs: _i, users: _u, ...rest } = when
  return rest
}

/** Whether a layer's stored targets name installs or app users. */
export const hasSubjects = (when: LayerWhen): boolean =>
  SUBJECT_KEYS.some((k) => (when[k]?.length ?? 0) > 0)

export function rolloutDraft(layer: Layer | undefined, id: InstrumentId): RolloutDraft {
  return {
    percent: layer?.state === 'active' ? (layer.rollout?.percent ?? 100) : 0,
    holdbackPercent: layer?.rollout?.holdback_percent ?? HOLDBACK_DEFAULT,
    when: layer ? withHints(layer) : {},
    guardrails: layer?.guardrails ?? { min_sessions: MIN_SESSIONS_DEFAULT },
    settings: layer ? settingsOf(layer, id) : defaultSettings(id),
    expiresAt: layer?.expires_at ?? null,
    subjects: 'kept',
  }
}

/** The layer an instrument's rollout writes; `name` is the instrument's, as the page words it. */
export function toLayer(
  id: InstrumentId,
  name: string,
  r: RolloutDraft,
  held: Pick<LayerBody, 'id'> | undefined,
): LayerBody {
  const x = byId(id)
  return {
    ...(held?.id ? { id: held.id } : {}),
    name,
    when: r.subjects === 'kept' ? withoutSubjects(r.when) : r.when,
    rollout: { percent: r.percent, holdback_percent: r.holdbackPercent },
    expires_at: r.expiresAt,
    changes: {
      ...(x?.family ? { instrumentation: { families: { [x.family]: r.settings.family } } } : {}),
      ...(x?.capture && r.settings.capture !== undefined
        ? { capture: { [x.capture]: r.settings.capture } }
        : {}),
    },
    ...(r.holdbackPercent > 0 ? { guardrails: r.guardrails } : {}),
  }
}

/** The app's active layers as PUT takes them, in policy order: the body publishing starts from. */
export function activeBodies(layers: readonly Layer[]): LayerBody[] {
  return layers
    .filter((l) => l.state === 'active')
    .map((l) => ({
      id: l.id,
      name: l.name,
      // Targets only: left out, the API keeps the layer's installs and users (read as hints).
      when: l.when,
      rollout: l.rollout,
      expires_at: l.expires_at,
      changes: l.changes,
      ...((l.rollout?.holdback_percent ?? 0) > 0 ? { guardrails: l.guardrails } : {}),
    }))
}

/**
 * A PUT body with this instrument's rollout in place: its layer replaced where it stands (the
 * body's order is the policy's), or a new one appended; at 0 % it is left out, and the API stops
 * it. Applied to the body, so several instruments' rollouts publish at once.
 */
export function withRollout(
  body: readonly LayerBody[],
  id: InstrumentId,
  name: string,
  r: RolloutDraft,
): LayerBody[] {
  const mine = body.find((l) => instrumentsOf(l).includes(id))
  if (r.percent <= 0) return body.filter((l) => l !== mine)
  const next = toLayer(id, name, r, mine)
  if (!mine) return [...body, next]
  return body.map((l) => (l === mine ? next : l))
}

/** A targeting id as a chip shows it: its last characters, the API never returns more (APS-426). */
export const idHint = (x: string): string => (x.length > 4 ? `…${x.slice(-4)}` : x)

/** The rollout stop at or below a percent, so a value between stops sits between them. */
export function stopIndex(percent: number): number {
  let i = 0
  ROLLOUT_STOPS.forEach((s, j) => {
    if (s <= percent) i = j
  })
  return i
}
